import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import {
  MAX_PROJECT_NAME_LENGTH,
  PROJECT_ROUTE,
  PURL_SAFE_HELP_MESSAGE,
} from "Constants";
import { useProjectsRoute } from "Hooks";
import { ProjectSummary } from "Types";
import { apiErrorToast, errorIsFetchError } from "Utils";
import * as yup from "Utils/yup";
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
} from "../../services/projects.api";
import { CreateProjectRequest, CreateProjectResponse } from "../../types";

type CreateProjectFormData = Omit<CreateProjectRequest["body"], "group_id">;

const createProjectSchema: yup.SchemaOf<CreateProjectFormData> = yup.object({
  name: yup
    .string()
    .required("Project name is required")
    .noInvisibleString("Project name should not be blank")
    .purlSafe()
    .max(
      MAX_PROJECT_NAME_LENGTH,
      `Project name must be at most ${MAX_PROJECT_NAME_LENGTH} characters`,
    ),
  description: yup
    .string()
    .optional()
    .max(500, "Max description length is 500 characters"),
});

type CreateProjectProps = {
  groupId?: string;
  project?: ProjectSummary;
  isOpen: boolean;
  onClose: () => void;
};

const CreateProjectContent = ({
  groupId,
  project,
  onClose,
}: Omit<CreateProjectProps, "isOpen">): React.JSX.Element => {
  const projectsRoute = useProjectsRoute();
  const navigate = useNavigate();
  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const methods = useForm<CreateProjectFormData>({
    resolver: yupResolver(createProjectSchema),
    defaultValues: {
      description: project?.description || "",
      name: project?.name || "",
    },
  });
  const {
    formState: { errors, isDirty },
    handleSubmit,
    setError,
  } = methods;

  const onSubmit: SubmitHandler<CreateProjectFormData> = async (data) => {
    try {
      let result: CreateProjectResponse | null = null;

      if (groupId) {
        result = await createProject({
          body: {
            ...data,
            group_id: groupId,
          },
        }).unwrap();
      } else if (project) {
        if (!isDirty) {
          onClose();
          return;
        }
        await updateProject({
          projectId: project.project_id,
          body: data,
        }).unwrap();
      }

      Toast.show({
        title: `Project ${project ? "edited" : "created"}`,
        type: "success",
      });

      if (result?.project_id) {
        navigate(`${projectsRoute}/${PROJECT_ROUTE}/${result.project_id}`);
      }

      onClose();
    } catch (error) {
      const isNameConflict =
        errorIsFetchError(error) &&
        typeof error.status === "number" &&
        error.status >= 400 &&
        error.status < 500;

      if (isNameConflict) {
        setError("name", {
          message:
            "A project with this name already exists in this group. Choose a different name.",
        });
      } else {
        apiErrorToast(
          error,
          `Could not ${project ? "edit" : "create"} project`,
        );
      }
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          {project ? "Edit" : "Create"} Project
        </Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                {project && (
                  <Dialog.Banner type="warning" title="Report URL change">
                    Renaming your project will also change the report URL for
                    all versions inside it. If you shared the old link, it will
                    no longer work.
                  </Dialog.Banner>
                )}
                <Form.TextField
                  name="name"
                  label="Project name"
                  id="project-name"
                  isRequired
                  errors={errors}
                  helperText={PURL_SAFE_HELP_MESSAGE}
                />
                <Form.TextArea
                  name="description"
                  label="Short description"
                  id="project-description"
                  errors={errors}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button id="cancel" color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                id={project ? "edit" : "create"}
                type="submit"
                color="primary"
              >
                {project ? "Edit" : "Create"}
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const CreateProject = ({
  isOpen,
  ...props
}: CreateProjectProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <CreateProjectContent {...props} />
  </Dialog.Frame>
);

export default CreateProject;
